import type { RecordSchema } from '@/types'
import { createQueryParam } from '~/utils/merge'

export const useStreamReader = () => {
	// const blob = new Blob([new Uint8Array(10 * 1024 * 1024)]) // any Blob, including a File
	const uploadProgress = ref(0)
	const error = shallowRef<string | null>(null)
	const pending = shallowRef(false)
	const config = useRuntimeConfig()
	const { cookie } = useAuth()
	const { isValidJSON } = useUtils()

	async function fetchStream<T = unknown>(
		url: string,
		options: {
			methods?: 'PUT' | 'POST' | 'GET' | 'DELETE' | 'PATCH'
			body?: RecordSchema | FormData | null
			baseUrl?: string
			query?: RecordSchema
		}
	) {
		const xhr = new XMLHttpRequest()
		const result = await new Promise((resolve, reject) => {
			pending.value = true
			uploadProgress.value = 0
			xhr.upload.addEventListener('progress', event => {
				error.value = null
				if (event.lengthComputable) {
					uploadProgress.value += Math.ceil(event.loaded / event.total) * 80
				}
			})

			xhr.addEventListener('loadend', () => {
				const result = isValidJSON(xhr.response) ? JSON.parse(xhr.response) : null

				if (xhr.status > 300) {
					if (result) {
						reject(result.responseText)
						useNotification().alert({
							status: xhr.status,
							message: result.responseText
						})
						error.value = result.responseText
						throw new Error(result.responseText)
					}
					reject(xhr.response)
					error.value = xhr.response
					useNotification().alert({
						status: xhr.status,
						message: 'Something Went Wrong'
					})
				} else {
					pending.value = false
					resolve(xhr.readyState === 4 ? result : null)
				}
				pending.value = false
			})

			xhr.open(
				options?.methods || 'GET',
				`${options?.baseUrl || config.public.APIUrlBase}${url}${options?.query ? '?' : ''}${createQueryParam(options?.query ?? {})}`,
				true
			)

			xhr.setRequestHeader('Authorization', `Bearer ${cookie.value}`)
			xhr.send(options?.body as unknown as XMLHttpRequestBodyInit)
		})
		return result as Promise<IRequestResponse<T>>
	}

	return {
		fetchStream,
		uploadProgress,
		error,
		pending
	}
}
